import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  CircularProgress,
  Breadcrumbs,
  Link,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Divider,
  TextField,
  Tooltip,
  Chip,
  Modal,
  IconButton,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  EditCalendar as EditCalendarIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { notification } from "src/utils/messages";
import {
  getChapterDirectors,
  getChapterDirectorLogs,
  assignChapterDirectors,
  getDirectorHistoryLog,
  addDirectorHistoryLog,
  updateDirectorHistoryLog,
  updateChapterDirectorTenure,
  deleteDirectorHistoryLog,
} from "src/api/commonapi";
import MemberPickerModal from "src/components/MemberPickerModal";
import Page from "src/components/Page";
import { Routes as ApiRoutes } from "src/constant/api_url";
import moment from "moment";
import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import DataTable from "react-data-table-component";
import CustomLoader from "src/commonComponents/CustomLoader";
import Label from "src/components/Label";
import { CSVLink } from "react-csv";

// History role labels.
//
// `role` alone cannot distinguish these: the fixed Chapter Director and every
// Additional Director are both stored as role = 1 (role = 2 is the fixed Support
// Director slot). The discriminator is is_fixed_slot — 1 for the fixed
// Chapter/Support Director tenure, 0 for an Additional Director.
//
// Per the agreed decision, "Director" and "Support Director" are never shown:
// both fixed slots read as "Chapter Director", and everything else reads as
// "Additional Director".
//
// Rows written before the tenure work have no is_fixed_slot value; those were all
// Additional Directors, so a missing value is treated as 0.
const roleLabel = (row) => {
  const isFixedSlot = parseInt(String(row?.is_fixed_slot ?? 0), 10) === 1;
  return isFixedSlot ? "Chapter Director" : "Additional Director";
};
const toDDMMYYYY = (date) => (date ? moment(date).format("DD-MM-YYYY") : "");

// Shared joining / expiry / notes editor. The fixed Chapter and Support Director
// cards and the Additional Director dialogs all use it, so every tenure on this
// screen is captured the same way and the flow stays identical between them.
function TenureFields({ joiningDate, expiryDate, notes, onChange, disabled = false }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Stack direction="row" spacing={1.25}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4 }}>
            Joining Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              inputFormat="dd/MM/yyyy"
              value={joiningDate}
              onChange={(newDate) => onChange({ joiningDate: newDate || null })}
              renderInput={(params) => <TextField {...params} size="small" fullWidth disabled={disabled} />}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4 }}>
            Expiry Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              inputFormat="dd/MM/yyyy"
              value={expiryDate}
              onChange={(newDate) => onChange({ expiryDate: newDate || null })}
              renderInput={(params) => <TextField {...params} size="small" fullWidth disabled={disabled} />}
            />
          </LocalizationProvider>
        </Box>
      </Stack>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4 }}>
          Notes
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Optional"
          disabled={disabled}
          inputProps={{ maxLength: 500 }}
        />
      </Box>
    </Box>
  );
}

// Renders "DD-MM-YYYY – DD-MM-YYYY" / "– Serving" for a tenure row, with a hint
// when the row is a read-only backfill or has no tenure record at all.
function TenureSummary({ row }) {
  if (!row) return null;
  const backfilled = parseInt(String(row.is_backfilled || 0), 10) === 1;
  const hasTenure = parseInt(String(row.has_tenure ?? 1), 10) === 1;
  if (!row.joining_date && !row.expiry_date && !backfilled && hasTenure) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {row.joining_date || "No joining date"} {row.expiry_date ? `– ${row.expiry_date}` : "– Serving"}
      </Typography>
      {row.notes && !backfilled && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25, fontStyle: "italic", overflowWrap: "anywhere" }}
        >
          {row.notes}
        </Typography>
      )}
      {backfilled && (
        <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
          Backfilled from chapter launch — read only
        </Typography>
      )}
      {!hasTenure && (
        <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
          No tenure record yet — set one on the next change
        </Typography>
      )}
    </Box>
  );
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  maxWidth: "95vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 10,
  borderRadius: "10px",
  p: 2,
};

const historyTableStyles = {
  rows: { style: { minHeight: "64px" } },
  headCells: { style: { paddingTop: "14px", paddingBottom: "14px", fontWeight: 700, backgroundColor: "#f8fafc" } },
  cells: { style: { paddingTop: "14px", paddingBottom: "14px", lineHeight: 1.5 } },
};

export default function ChapterDirectors() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  let loginUser = null;
  try {
    loginUser = JSON.parse(localStorage.getItem("userData"));
  } catch (error) {
    loginUser = null;
  }
  const adminId = Number(loginUser?.u_id ?? loginUser?.m_id ?? 0) || 0;

  const hasAccess =
    loginUser?.u_type == 0 ||
    loginUser?.u_type == 1 ||
    (loginUser?.p_user_id == 1 && loginUser?.u_type == 2 && loginUser?.is_subadmin == 1) ||
    (loginUser?.p_user_id == 1 && loginUser?.u_type == 2 && loginUser?.management_p != 0);

  useEffect(() => {
    if (!loginUser || !adminId) navigate("/login", { replace: true });
    else if (!hasAccess) navigate("/dashboard", { replace: true });
  }, [hasAccess, navigate]);

  const [chapter, setChapter] = useState(null);
  const [current, setCurrent] = useState({ director: null, support_director: null });
  const [extra, setExtra] = useState([]); // active extra directors from director_history_log (unlimited, + button)
  const [history, setHistory] = useState([]); // preview only (max 5) from getchapterdirectors — NOT used for table pagination
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingExtra, setIsAddingExtra] = useState(false);

  // Paginated Director History log (new split API) — table source
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0); // MUI 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);
  const searchTimerRef = React.useRef(null);

  // staged: Record<role, { memberId, memberName, photo, previousExpiry: Date|null }>
  // undefined = untouched, { memberId: 0 } = vacate, { memberId: number } = replace/add
  const [staged, setStaged] = useState({});

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState(null);
  const [removeModal, setRemoveModal] = useState({ open: false, role: null });
  const [vacateExpiry, setVacateExpiry] = useState(new Date());
  const [fullHistoryOpen, setFullHistoryOpen] = useState(false);
  const [fullHistory, setFullHistory] = useState([]);
  const [fullHistoryLoading, setFullHistoryLoading] = useState(false);
  // Extra director add flow (unlimited, history table)
  const [extraPickerOpen, setExtraPickerOpen] = useState(false);
  const [extraDetailOpen, setExtraDetailOpen] = useState(false);
  const [extraPendingMember, setExtraPendingMember] = useState(null);
  const [extraForm, setExtraForm] = useState({ joiningDate: new Date(), expiryDate: null, notes: "" });
  const [changeExtraTarget, setChangeExtraTarget] = useState(null);
  const [changeExtraPickerOpen, setChangeExtraPickerOpen] = useState(false);
  const [editExtraRow, setEditExtraRow] = useState(null);
  const [editExtraOpen, setEditExtraOpen] = useState(false);
  const [editExtraForm, setEditExtraForm] = useState({ joiningDate: new Date(), expiryDate: null, notes: "" });
  const [deleteExtraTarget, setDeleteExtraTarget] = useState(null);
  // Edit tenure of a FIXED Chapter Director slot (Director / Support Director).
  // Saved via /updatechapterdirectortenure which closes the old tenure row and
  // inserts a new history row, so the Director History table keeps the trail.
  const [editFixedOpen, setEditFixedOpen] = useState(false);
  const [editFixedRole, setEditFixedRole] = useState(null);
  const [editFixedRow, setEditFixedRow] = useState(null);
  const [editFixedForm, setEditFixedForm] = useState({ joiningDate: new Date(), expiryDate: null, notes: "" });
  const [isSavingFixed, setIsSavingFixed] = useState(false);

  // Keep getchapterdirectors for header/cards only — do NOT use its history for table pagination.
  // It also carries the fixed slots' tenure (dh_id, joining_date, expiry_date, notes,
  // has_tenure, is_backfilled), which the fixed Director / Support Director cards show.
  const fetchData = useCallback(async () => {
    if (!chapterId || !adminId) return;
    setIsLoading(true);
    try {
      const res = await getChapterDirectors({
        chpt_id: String(chapterId),
      });
      if (res?.status == 1 && res?.data) {
        const data = res.data;
        setChapter(data.chapter || null);
        const cur = data.current || {};
        setCurrent({
          director: cur.director !== undefined ? cur.director : null,
          support_director: cur.support_director ?? cur.supportDirector ?? null,
        });
        const extraRows = Array.isArray(data.extra) ? data.extra : [];
        setExtra(extraRows);
        // history only for preview (max 5) — backend is hard LIMITED 5, ignores history_limit
        const hist = Array.isArray(data.history) ? data.history : Array.isArray(data?.data?.history) ? data.data.history : [];
        setHistory(hist);
      } else {
        notification({ type: "error", message: res?.message || "Failed to load directors" })();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to load directors" })();
    } finally {
      setIsLoading(false);
    }
  }, [chapterId, adminId]);

  const fetchLogs = useCallback(async () => {
    if (!chapterId) return;
    setLogsLoading(true);
    try {
      const res = await getChapterDirectorLogs({
        chpt_id: Number(chapterId),
        page: page + 1,
        per_page: rowsPerPage,
        search: search.trim() || undefined,
      });
      if (res?.status === 1) {
        setLogs(res?.data || []);
        setTotal(res?.total ?? res?.pagination?.total ?? 0);
      } else {
        setLogs([]);
        setTotal(0);
      }
    } catch (e) {
      setLogs([]);
      setTotal(0);
    } finally {
      setLogsLoading(false);
    }
  }, [chapterId, page, rowsPerPage, search]);

  useEffect(() => {
    if (chapterId) fetchData();
  }, [chapterId, fetchData]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearch(val);
      setPage(0);
    }, 400);
  };

  const getCurrentForRole = (role) => (role == 1 ? current.director : current.support_director);
  const isStaged = (role) => staged[role] !== undefined;
  const effectiveHolder = (role) => {
    if (staged[role] !== undefined) {
      const s = staged[role];
      if (s.memberId === 0) return null;
      return { m_id: s.memberId, m_name: s.memberName, photo: s.photo };
    }
    return getCurrentForRole(role);
  };

  const handlePickerOpen = (role) => {
    setPickerRole(role);
    setPickerOpen(true);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
    setPickerRole(null);
  };

  const handlePickerConfirm = (selectedIds, selectedMembers) => {
    if (!selectedIds || selectedIds.length === 0) return;
    const memberId = selectedIds[0];
    const role = pickerRole;
    const picked = selectedMembers?.find((m) => m.m_id == memberId);
    // Prefer full data from picker; fallback to history/current/extra lookup
    const found =
      picked ||
      history.find((h) => h.m_id == memberId) ||
      (current.director?.m_id == memberId ? current.director : null) ||
      (current.support_director?.m_id == memberId ? current.support_director : null) ||
      extra.find((e) => e.m_id == memberId);
    const stagedEntry = {
      memberId,
      memberName: found?.m_name || `Member ${memberId}`,
      photo: found?.photo || null,
      businessName: found?.business_name || found?.display_name || "",
      m_verify_status: found?.m_verify_status ?? null,
      // Tenure for the incoming occupant. Defaults to today, which is what the
      // backend used to assume implicitly, but is now explicit and editable.
      joiningDate: new Date(),
      expiryDate: null,
      notes: "",
    };
    setStaged((prev) => ({ ...prev, [role]: stagedEntry }));
    handlePickerClose();
  };

  // Patch the staged tenure of a fixed slot. Clears the expiry if it would fall
  // before the joining date, so the payload never trips the backend's
  // "expiry_date must be on or after joining_date" rejection.
  const handleStagedTenureChange = (role, patch) => {
    setStaged((prev) => {
      const cur = prev[role];
      if (!cur) return prev;
      const next = { ...cur, ...patch };
      if (next.joiningDate && next.expiryDate && moment(next.expiryDate).isBefore(moment(next.joiningDate), "day")) {
        next.expiryDate = null;
      }
      return { ...prev, [role]: next };
    });
  };

  const handleVacateClick = (role) => {
    const holder = getCurrentForRole(role);
    if (!holder) return;
    setRemoveModal({ open: true, role });
    setVacateExpiry(new Date());
  };

  const handleVacateCancel = () => setRemoveModal({ open: false, role: null });

  const handleVacateConfirm = () => {
    const role = removeModal.role;
    if (!vacateExpiry) {
      notification({ type: "error", message: "Please select previous expiry date" })();
      return;
    }
    setStaged((prev) => ({
      ...prev,
      [role]: { memberId: 0, memberName: "", previousExpiry: vacateExpiry },
    }));
    handleVacateCancel();
  };

  const handleRevert = (role) => {
    setStaged((prev) => {
      const next = { ...prev };
      delete next[role];
      return next;
    });
  };

  const buildAssignments = () => {
    return Object.entries(staged)
      .filter(([, v]) => v !== undefined)
      .map(([roleStr, v]) => {
        const role = Number(roleStr);
        if (v.memberId === 0) {
          return {
            role,
            member_id: null,
            previous_expiry: v.previousExpiry ? toDDMMYYYY(v.previousExpiry) : toDDMMYYYY(new Date()),
          };
        }
        // Fixed Director / Support Director: the tenure is now recorded in
        // director_history_log (is_fixed_slot = 1) alongside the chepters
        // pointer, so joining / expiry / notes are sent like the Additional
        // Director flow. Backend closes the outgoing tenure automatically.
        return {
          role,
          member_id: String(v.memberId),
          joining_date: v.joiningDate ? toDDMMYYYY(v.joiningDate) : "",
          expiry_date: v.expiryDate ? toDDMMYYYY(v.expiryDate) : "",
          notes: v.notes || "",
        };
      });
  };

  const handleSaveAll = async () => {
    const assignments = buildAssignments();
    if (assignments.length === 0) {
      notification({ type: "error", message: "No changes to save" })();
      return;
    }
    const memberIds = assignments.filter((a) => a.member_id).map((a) => a.member_id);
    if (memberIds.length === 2 && memberIds[0] === memberIds[1]) {
      notification({ type: "error", message: "Same member cannot hold both Director roles" })();
      return;
    }
    setIsSaving(true);
    try {
      const res = await assignChapterDirectors({
        chpt_id: String(chapterId),
        m_id: String(adminId),
        assignments,
      });
      notification({ type: res?.status == 0 ? "error" : "success", message: res?.message || (res?.status == 1 ? "Directors updated" : "Update failed") })();
      if (res?.status == 1) {
        const cur = res?.data?.current;
        if (cur) {
          setCurrent({
            director: cur.director ?? cur.director === null ? cur.director : current.director,
            support_director: cur.support_director ?? cur.supportDirector ?? null,
          });
        }
        setStaged({});
        fetchData();
        fetchLogs();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to update directors" })();
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewAll = () => {
    setPage(0);
    setFullHistoryOpen(true);
  };

  // Extra directors: plus-button flow → director_history_log (not chepters batch)
  const handleExtraPickConfirm = (selectedIds, selectedMembers) => {
    if (!selectedIds || selectedIds.length === 0) return;
    const memberId = selectedIds[0];
    const picked = selectedMembers?.find((m) => m.m_id == memberId);
    setExtraPickerOpen(false);
    setExtraPendingMember({
      m_id: memberId,
      m_name: picked?.m_name || `Member ${memberId}`,
      photo: picked?.photo || null,
      business_name: picked?.business_name || picked?.display_name || "",
      m_verify_status: picked?.m_verify_status ?? null,
    });
    setExtraForm({ joiningDate: new Date(), notes: "" });
    setExtraDetailOpen(true);
  };

  const handleChangeExtraPickConfirm = async (selectedIds, selectedMembers) => {
    if (!selectedIds || selectedIds.length === 0 || !changeExtraTarget) return;
    const memberId = selectedIds[0];
    const picked = selectedMembers?.find((m) => m.m_id == memberId);
    setChangeExtraPickerOpen(false);
    const row = changeExtraTarget;
    const payload = {
      dh_id: row.dh_id,
      chpt_id: String(chapterId),
      member_id: String(memberId),
      role: row.role,
      joining_date: row.joining_date,
      expiry_date: row.expiry_date || "",
      notes: row.notes || "",
    };
    if (picked?.m_name) payload.m_name = picked.m_name;
    try {
      const res = await updateDirectorHistoryLog(payload);
      notification({ type: res?.status == 0 ? "error" : "success", message: res?.message || (res?.status == 1 ? "Director updated" : "Update failed") })();
      if (res?.status == 1) {
        fetchData();
        fetchLogs();
      }
    } catch (e) {
      notification({ type: "error", message: "Failed to change director" })();
    } finally {
      setChangeExtraTarget(null);
    }
  };

  // Open the tenure editor for a FIXED Chapter Director slot. The card's holder
  // object carries the active tenure (joining_date / expiry_date / notes) plus
  // has_tenure / is_backfilled, so the form can be prefilled from it.
  const openEditFixed = (role) => {
    const holder = role === 2 ? current.support_director : current.director;
    if (!holder) return;
    setEditFixedRole(role);
    setEditFixedRow(holder);
    setEditFixedForm({
      joiningDate: holder.joining_date ? moment(holder.joining_date, "DD-MM-YYYY").toDate() : new Date(),
      expiryDate: holder.expiry_date ? moment(holder.expiry_date, "DD-MM-YYYY").toDate() : null,
      notes: holder.notes || "",
    });
    setEditFixedOpen(true);
  };

  const handleEditFixedSave = async () => {
    if (!editFixedRole) return;
    if (!editFixedForm.joiningDate) {
      notification({ type: "error", message: "Please select a joining date" })();
      return;
    }
    if (editFixedForm.expiryDate && moment(editFixedForm.expiryDate).isBefore(moment(editFixedForm.joiningDate), "day")) {
      notification({ type: "error", message: "Expiry date must be on or after the joining date" })();
      return;
    }
    setIsSavingFixed(true);
    try {
      const res = await updateChapterDirectorTenure({
        chpt_id: String(chapterId),
        role: editFixedRole,
        joining_date: toDDMMYYYY(editFixedForm.joiningDate),
        expiry_date: editFixedForm.expiryDate ? toDDMMYYYY(editFixedForm.expiryDate) : "",
        notes: editFixedForm.notes || "",
        m_id: String(adminId),
      });
      notification({
        type: res?.status == 0 ? "error" : "success",
        message: res?.message || (res?.status == 1 ? "Chapter director tenure updated" : "Update failed")
      })();
      if (res?.status == 1) {
        setEditFixedOpen(false);
        setEditFixedRow(null);
        setEditFixedRole(null);
        fetchData();
        fetchLogs();
      }
    } catch (e) {
      notification({ type: "error", message: "Failed to update chapter director tenure" })();
    } finally {
      setIsSavingFixed(false);
    }
  };

  const openEditExtra = (row) => {
    setEditExtraRow(row);
    setEditExtraForm({
      joiningDate: row.joining_date ? moment(row.joining_date, "DD-MM-YYYY").toDate() : new Date(),
      expiryDate: row.expiry_date ? moment(row.expiry_date, "DD-MM-YYYY").toDate() : null,
      notes: row.notes || "",
    });
    setEditExtraOpen(true);
  };

  const handleEditExtraSave = async () => {
    if (!editExtraRow) return;
    if (editExtraForm.expiryDate && moment(editExtraForm.expiryDate).isBefore(moment(editExtraForm.joiningDate), "day")) {
      notification({ type: "error", message: "Expiry date must be on or after the joining date" })();
      return;
    }
    try {
      const res = await updateDirectorHistoryLog({
        dh_id: editExtraRow.dh_id,
        chpt_id: String(chapterId),
        role: editExtraRow.role || 1,
        joining_date: toDDMMYYYY(editExtraForm.joiningDate),
        expiry_date: editExtraForm.expiryDate ? toDDMMYYYY(editExtraForm.expiryDate) : "",
        notes: editExtraForm.notes || "",
      });
      notification({ type: res?.status == 0 ? "error" : "success", message: res?.message || (res?.status == 1 ? "Director updated" : "Update failed") })();
      if (res?.status == 1) {
        setEditExtraOpen(false);
        setEditExtraRow(null);
        fetchData();
        fetchLogs();
      }
    } catch (e) {
      notification({ type: "error", message: "Failed to update director" })();
    }
  };

  const handleDeleteExtra = async () => {
    if (!deleteExtraTarget) return;
    try {
      const res = await deleteDirectorHistoryLog({ dh_id: deleteExtraTarget.dh_id, chpt_id: String(chapterId) });
      notification({ type: res?.status == 0 ? "error" : "success", message: res?.message || (res?.status == 1 ? "Director deleted" : "Delete failed") })();
      if (res?.status == 1) {
        fetchData();
        fetchLogs();
      }
    } catch (e) {
      notification({ type: "error", message: "Failed to delete director" })();
    } finally {
      setDeleteExtraTarget(null);
    }
  };

  const handleExtraDetailAdd = async () => {
    if (!extraPendingMember?.m_id || !extraForm.joiningDate) {
      notification({ type: "error", message: "Please select member and joining date" })();
      return;
    }
    if (extraForm.expiryDate && moment(extraForm.expiryDate).isBefore(moment(extraForm.joiningDate), "day")) {
      notification({ type: "error", message: "Expiry date must be on or after the joining date" })();
      return;
    }
    setIsAddingExtra(true);
    try {
      const res = await addDirectorHistoryLog({
        chpt_id: String(chapterId),
        m_id: String(adminId),
        member_id: String(extraPendingMember.m_id),
        joining_date: toDDMMYYYY(extraForm.joiningDate),
        // Sent explicitly rather than hardcoded "" so a new additional director
        // can be created with a fixed end date, same as the fixed slots.
        expiry_date: extraForm.expiryDate ? toDDMMYYYY(extraForm.expiryDate) : "",
        role: 1,
        notes: extraForm.notes || "",
      });
      notification({ type: res?.status == 0 ? "error" : "success", message: res?.message || (res?.status == 1 ? "Extra director added" : "Add failed") })();
      if (res?.status == 1) {
        setExtraDetailOpen(false);
        setExtraPendingMember(null);
        fetchData();
        fetchLogs();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to add extra director" })();
    } finally {
      setIsAddingExtra(false);
    }
  };

  const getPhotoUrl = (photo) => (photo ? `${ApiRoutes.API_HOSTNAME}images/member/${photo}` : null);

  const renderDirectorCard = (role) => {
    const holder = getCurrentForRole(role);
    const stagedEntry = staged[role];
    const isDirty = isStaged(role);
    const isVacate = isDirty && stagedEntry?.memberId === 0;
    const isChange = isDirty && !isVacate;

    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: isDirty ? "primary.main" : "divider",
          bgcolor: "background.paper",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: "1 0 300px",
          minWidth: 300,
          transition: "border-color 0.15s ease",
        }}
      >
        {/* Backfilled marker. The role heading was removed — the Chapter Directors
            heading above already covers both cards, and "Director" /
            "Support Director" are not shown anywhere per the agreed decision. */}
        {holder && parseInt(String(holder.is_backfilled || 0), 10) === 1 && (
          <Box sx={{ alignSelf: "flex-start", mt: -0.5 }}>
            <Chip label="Backfilled" size="small" sx={{ height: 20, fontSize: "0.65rem" }} />
          </Box>
        )}

        {/* Holder / Vacant — like AllocatePostPage: always show the current holder (dimmed when pending) */}
        {holder ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, opacity: isDirty ? 0.55 : 1 }}>
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar src={getPhotoUrl(holder.photo)} sx={{ width: 48, height: 48, border: "2px solid", borderColor: "primary.light" }}>
                {(holder.m_name || "?").charAt(0).toUpperCase()}
              </Avatar>
              {(holder.m_verify_status === 1 || holder.m_verify_status === 2) && (
                <Box
                  component="img"
                  src={holder.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
                  alt="Verified"
                  sx={{ width: 18, height: 18, position: "absolute", top: -2, right: -2 }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.25 }}>
                {holder.m_name}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {holder.business_name || holder.display_name || ""}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 2, border: "1px dashed", borderColor: "divider", borderRadius: 1.5, bgcolor: "grey.50", opacity: isChange ? 0.55 : 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Vacant
            </Typography>
            {!isChange && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => handlePickerOpen(role)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add
              </Button>
            )}
          </Box>
        )}

        {/* Current tenure of the occupant in this slot */}
        {holder && !isDirty && <TenureSummary row={holder} />}

        {isChange && (
          <Box sx={{ borderTop: "1px dashed", borderColor: "divider", pt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
              Will update to:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar src={getPhotoUrl(stagedEntry.photo)} sx={{ width: 32, height: 32 }}>
                  {(stagedEntry.memberName || "?").charAt(0).toUpperCase()}
                </Avatar>
                {(stagedEntry.m_verify_status === 1 || stagedEntry.m_verify_status === 2) && (
                  <Box
                    component="img"
                    src={stagedEntry.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
                    alt="Verified"
                    sx={{ width: 14, height: 14, position: "absolute", top: -2, right: -2 }}
                  />
                )}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {stagedEntry.memberName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {stagedEntry.businessName || ""}
                </Typography>
              </Box>
              <Chip label="New" size="small" color="success" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }} />
            </Box>
            <TenureFields
              joiningDate={stagedEntry.joiningDate}
              expiryDate={stagedEntry.expiryDate}
              notes={stagedEntry.notes}
              onChange={(patch) => handleStagedTenureChange(role, patch)}
            />
          </Box>
        )}

        {isVacate && holder && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1, bgcolor: "warning.lighter" }}>
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar src={getPhotoUrl(holder.photo)} sx={{ width: 28, height: 28 }}>
                {(holder.m_name || "?").charAt(0).toUpperCase()}
              </Avatar>
              {(holder.m_verify_status === 1 || holder.m_verify_status === 2) && (
                <Box
                  component="img"
                  src={holder.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
                  alt="Verified"
                  sx={{ width: 12, height: 12, position: "absolute", top: -2, right: -2 }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600, display: "block" }}>
                {holder.m_name} will be removed
              </Typography>
              <Typography variant="caption" sx={{ color: "warning.dark", display: "block" }}>
                {holder.business_name || holder.display_name || ""}
                {stagedEntry?.previousExpiry ? ` • Expiry ${toDDMMYYYY(stagedEntry.previousExpiry)}` : ""}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {isDirty ? (
            <>
              <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600 }}>
                {isVacate ? "Removal pending" : "Change pending"}
              </Typography>
              <Button size="small" onClick={() => handleRevert(role)} sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600 }}>
                Revert
              </Button>
            </>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                {holder ? "Active" : "No director assigned"}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                {holder && (
                  <Tooltip title="Edit joining date, expiry date and notes">
                    <IconButton
                      size="small"
                      onClick={() => openEditFixed(role)}
                      sx={{ color: "primary.main", p: 0.5, borderRadius: 1 }}
                    >
                      <EditCalendarIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {holder && (
                  <Tooltip title="Remove from position">
                    <Button size="small" color="error" onClick={() => handleVacateClick(role)} sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, minWidth: 0, px: 1 }}>
                      Remove
                    </Button>
                  </Tooltip>
                )}
                <Button size="small" color="primary" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => handlePickerOpen(role)} sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600 }}>
                  {holder ? "Change" : "Add"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const historyColumns = [
    { name: "Director", selector: (r) => r.m_name, wrap: true, sortable: true, width: "300px" },
    { name: "Role", selector: (r) => roleLabel(r), sortable: true, width: "160px" },
    { name: "Joining", selector: (r) => r.joining_date || "-", sortable: true, width: "110px" },
    { name: "Expiry", selector: (r) => r.expiry_date || "Serving", sortable: true, width: "110px" },
    {
      name: "Notes",
      selector: (r) => r.notes || "",
      sortable: true,
      width: "260px",
      wrap: true,
      cell: (r) => (
        <Typography
          variant="caption"
          color={r.notes ? "text.secondary" : "text.disabled"}
          sx={{ whiteSpace: "normal", overflowWrap: "anywhere", fontStyle: r.notes ? "italic" : "normal" }}
        >
          {r.notes || "-"}
        </Typography>
      ),
    },
    {
      name: "Status",
      width: "110px",
      cell: (r) => (
        <Label variant="ghost" color={r.status == 1 ? "success" : "default"}>
          {r.status == 1 ? "Active" : "Past"}
        </Label>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Page title="Chapter Directors | LVB Admin">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  const hasDirty = Object.keys(staged).length > 0;

  return (
    <Page title="Chapter Directors | LVB Admin">
      <Container maxWidth="xl">
        <Breadcrumbs separator="›" sx={{ mb: 2 }}>
          <Link component="button" variant="body2" onClick={() => navigate("/chapter")} sx={{ color: "text.secondary", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
            Chapter
          </Link>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
            Directors
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Chapter Directors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chapter ? `Chapter: ${chapter.chpt_name}${chapter.city_name ? ` • ${chapter.city_name}` : ""}` : `Chapter #${chapterId}`} — Manage chapter directors (position is fixed, occupant changes).
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/chapter")}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveAll}
              disabled={isSaving || !hasDirty}
            >
              {isSaving ? "Saving..." : "Update All"}
            </Button>
          </Stack>
        </Stack>

        {/* Chapter Directors — fixed positions, occupant changes */}
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Chapter Directors
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2.5,
              flexWrap: "wrap",
            }}
          >
            {renderDirectorCard(1)}
            {renderDirectorCard(2)}
          </Box>
        </Card>

        {/* Additional directors — unlimited, from director_history_log via + button (migrated flow) */}
        <Card sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box>
              <Typography variant="h6">Additional Director</Typography>
              <Typography variant="caption" color="text.secondary">
                Any additional director added will not be displayed in the application.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setExtraPickerOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Add Additional Director
            </Button>
          </Stack>
          {extra.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 1.5, bgcolor: "grey.50" }}>
              No additional directors yet — use “Add Additional Director” to add one.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.5, alignItems: "stretch" }}>
              {extra.map((row) => (
                <Box
                  key={row.dh_id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    width: "100%",
                    minWidth: 0,
                    minHeight: 78,
                    height: "auto",
                    boxSizing: "border-box",
                    alignSelf: "stretch",
                  }}
                >
                  <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Avatar src={getPhotoUrl(row.photo)} sx={{ width: 36, height: 36 }}>
                      {(row.m_name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    {(row.m_verify_status === 1 || row.m_verify_status === 2) && (
                      <Box
                        component="img"
                        src={row.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
                        alt="Verified"
                        sx={{ width: 14, height: 14, position: "absolute", top: -2, right: -2 }}
                      />
                    )}
                  </Box>
                  <Tooltip title={row.m_name} arrow placement="top">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: 1.25,
                        }}
                      >
                        {row.m_name}
                      </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.joining_date || ""} {row.expiry_date ? `– ${row.expiry_date}` : "– Serving"}
                    </Typography>
                    {row.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontStyle: "italic", overflowWrap: "anywhere" }}
                      >
                        {row.notes}
                      </Typography>
                    )}
                    </Box>
                  </Tooltip>
                  {/* Backfilled rows are read-only system entries — the backend
                      rejects edit and delete on them, so the controls are disabled. */}
                  {parseInt(String(row.is_backfilled || 0), 10) === 1 ? (
                    <Tooltip title="Backfilled system entry — read only" arrow placement="top">
                      <Chip label="Read only" size="small" sx={{ height: 22, fontSize: "0.65rem", flexShrink: 0 }} />
                    </Tooltip>
                  ) : (
                    <>
                      <IconButton size="small" onClick={() => { setChangeExtraTarget(row); setChangeExtraPickerOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Button size="small" onClick={() => openEditExtra(row)} sx={{ minWidth: 0, px: 0.5, textTransform: "none" }}>
                        Edit
                      </Button>
                      <IconButton size="small" color="error" onClick={() => setDeleteExtraTarget(row)}>
                        <Box component="span" sx={{ fontSize: "1.1rem", lineHeight: 1 }}>×</Box>
                      </IconButton>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Card>

        <Card sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6">Director History</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<HistoryIcon />} onClick={handleViewAll} variant="outlined" sx={{ textTransform: "none" }}>
                View all
              </Button>
              <CSVLink data={history.map((r) => ({ ...r, role_label: roleLabel(r) }))} headers={[{ label: "Director", key: "m_name" },{ label: "Role", key: "role_label" },{ label: "Joining", key: "joining_date" },{ label: "Expiry", key: "expiry_date" },{ label: "Status", key: "status" },{ label: "Notes", key: "notes" }]} filename={`Chapter_${chapterId}_Directors.csv`} style={{ textDecoration: "none" }}>
                <Button size="small" variant="outlined" sx={{ textTransform: "none" }}>Export CSV</Button>
              </CSVLink>
            </Stack>
          </Stack>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
              No director history for this chapter yet.
            </Typography>
          ) : (
            <DataTable
              columns={historyColumns}
              data={history}
              highlightOnHover
              customStyles={historyTableStyles}
              progressPending={isLoading}
              progressComponent={<CustomLoader />}
            />
          )}
        </Card>

        <MemberPickerModal
          open={pickerOpen}
          onClose={handlePickerClose}
          onConfirm={handlePickerConfirm}
          chapterId={chapterId}
          currentHolderId={pickerRole ? (getCurrentForRole(pickerRole)?.m_id || (staged[pickerRole]?.memberId || null)) : null}
          single
          useDirectorCandidates
          title="Select Director"
          subtitle="Choose a member for this position. Position is fixed."
          confirmLabel="Select"
        />

        <MemberPickerModal
          open={extraPickerOpen}
          onClose={() => setExtraPickerOpen(false)}
          onConfirm={handleExtraPickConfirm}
          chapterId={chapterId}
          single
          useDirectorCandidates
          title="Add Additional Director"
          subtitle="Pick a member to add as an additional director."
          confirmLabel="Select"
        />

        <MemberPickerModal
          open={changeExtraPickerOpen}
          onClose={() => { setChangeExtraPickerOpen(false); setChangeExtraTarget(null); }}
          onConfirm={handleChangeExtraPickConfirm}
          chapterId={chapterId}
          single
          useDirectorCandidates
          title="Change Director"
          subtitle={`Select new member for ${changeExtraTarget?.m_name || "this director"}.`}
          confirmLabel="Change"
        />

        <Dialog open={editExtraOpen} onClose={() => setEditExtraOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>Edit Director</DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Joining Date"
                  inputFormat="dd/MM/yyyy"
                  value={editExtraForm.joiningDate}
                  onChange={(d) => setEditExtraForm((p) => ({ ...p, joiningDate: d }))}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date"
                  inputFormat="dd/MM/yyyy"
                  value={editExtraForm.expiryDate}
                  onChange={(d) => setEditExtraForm((p) => ({ ...p, expiryDate: d }))}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
              <TextField label="Notes" size="small" fullWidth multiline rows={2} value={editExtraForm.notes} onChange={(e) => setEditExtraForm((p) => ({ ...p, notes: e.target.value }))} />
            </Stack>
            {parseInt(String(editExtraRow?.is_backfilled || 0), 10) === 1 && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
                This is a backfilled system entry. The backend only allows it to be changed into a real tenure, not removed.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setEditExtraOpen(false)} variant="outlined" sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button onClick={handleEditExtraSave} variant="contained" sx={{ textTransform: "none" }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit the TENURE of a fixed Chapter Director slot (Director / Support Director).
            Saving closes the previous tenure row and inserts a new one, so the change
            shows up in the Director History table instead of overwriting the past. */}
        <Dialog
          open={editFixedOpen}
          onClose={() => !isSavingFixed && setEditFixedOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            Edit {editFixedRole === 2 ? "Support Director" : "Chapter Director"} Tenure
          </DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            {editFixedRow && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {editFixedRow.m_name} &mdash; {chapter?.chpt_name || `Chapter #${chapterId}`}
              </Typography>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Joining Date"
                  inputFormat="dd/MM/yyyy"
                  value={editFixedForm.joiningDate}
                  onChange={(d) => setEditFixedForm((p) => ({ ...p, joiningDate: d }))}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date"
                  inputFormat="dd/MM/yyyy"
                  value={editFixedForm.expiryDate}
                  onChange={(d) => setEditFixedForm((p) => ({ ...p, expiryDate: d }))}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editFixedForm.notes}
                onChange={(e) => setEditFixedForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              Saving closes the current tenure and adds the updated one to the Director History, so the
              previous dates are kept.
            </Typography>
            {parseInt(String(editFixedRow?.is_backfilled || 0), 10) === 1 && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
                The current entry was backfilled from the chapter launch. Saving replaces it with a real
                tenure &mdash; the backfilled entry is kept in the history as a past record.
              </Typography>
            )}
            {parseInt(String(editFixedRow?.has_tenure ?? 1), 10) === 0 && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
                This chapter has no tenure record yet &mdash; saving creates the first one.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setEditFixedOpen(false)}
              variant="outlined"
              disabled={isSavingFixed}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditFixedSave}
              variant="contained"
              disabled={isSavingFixed}
              sx={{ textTransform: "none" }}
            >
              {isSavingFixed ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteExtraTarget !== null} onClose={() => setDeleteExtraTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>Delete Director?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Remove {deleteExtraTarget?.m_name || "this director"} (dh {deleteExtraTarget?.dh_id})? The tenure will be closed with today's date and kept in the history.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteExtraTarget(null)} variant="outlined" sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteExtra} variant="contained" color="error" sx={{ textTransform: "none" }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={extraDetailOpen} onClose={() => setExtraDetailOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>Add Additional Director</DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, p: 1, borderRadius: 1.5, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar src={getPhotoUrl(extraPendingMember?.photo)} sx={{ width: 40, height: 40 }}>
                  {(extraPendingMember?.m_name || "?").charAt(0).toUpperCase()}
                </Avatar>
                {(extraPendingMember?.m_verify_status === 1 || extraPendingMember?.m_verify_status === 2) && (
                  <Box
                    component="img"
                    src={extraPendingMember.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
                    alt="Verified"
                    sx={{ width: 16, height: 16, position: "absolute", top: -2, right: -2 }}
                  />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {extraPendingMember?.m_name || `Member ${extraPendingMember?.m_id || "—"}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {extraPendingMember?.business_name || ""}
                </Typography>
              </Box>
            </Box>
            <Stack spacing={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Joining Date *"
                  inputFormat="dd/MM/yyyy"
                  value={extraForm.joiningDate}
                  onChange={(d) => setExtraForm((p) => ({ ...p, joiningDate: d }))}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date"
                  inputFormat="dd/MM/yyyy"
                  value={extraForm.expiryDate || null}
                  onChange={(d) => setExtraForm((p) => ({ ...p, expiryDate: d }))}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
              <TextField label="Notes" size="small" fullWidth multiline rows={2} value={extraForm.notes} onChange={(e) => setExtraForm((p) => ({ ...p, notes: e.target.value }))} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setExtraDetailOpen(false)} variant="outlined" sx={{ textTransform: "none" }} disabled={isAddingExtra}>
              Cancel
            </Button>
            <Button onClick={handleExtraDetailAdd} variant="contained" sx={{ textTransform: "none" }} disabled={isAddingExtra} startIcon={isAddingExtra ? <CircularProgress size={16} color="inherit" /> : null}>
              {isAddingExtra ? "Adding..." : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={removeModal.open} onClose={handleVacateCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>Remove Director?</DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will remove the current director without a replacement. Set the end date for the outgoing director.
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Expiry Date *"
                inputFormat="dd/MM/yyyy"
                value={vacateExpiry}
                onChange={(d) => setVacateExpiry(d)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={handleVacateCancel} variant="outlined" sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button onClick={handleVacateConfirm} variant="contained" color="error" sx={{ textTransform: "none" }}>
              Confirm Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Modal open={fullHistoryOpen} onClose={() => setFullHistoryOpen(false)}>
          <Box sx={modalStyle}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6">Director History — {chapter?.chpt_name || `Chapter #${chapterId}`}</Typography>
              <TextField
                size="small"
                placeholder="Search director..."
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 220 }}
              />
            </Stack>
            <DataTable
              columns={historyColumns}
              data={logs}
              highlightOnHover
              customStyles={historyTableStyles}
              progressPending={logsLoading}
              progressComponent={<CustomLoader />}
              noDataComponent={<Typography sx={{ p: 3, textAlign: "center" }} color="text.secondary">No director history found.</Typography>}
            />
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]}
            />
            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => setFullHistoryOpen(false)}>
                Close
              </Button>
            </Stack>
          </Box>
        </Modal>
      </Container>
    </Page>
  );
}
